import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { jwtDecode } from 'jwt-decode';
import { authService } from '../services/authService';
import { User } from '../types';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  loading: boolean;
  sessionLoading: boolean;
  setSessionLoading: (loading: boolean) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionLoading, setSessionLoading] = useState(false);

  useEffect(() => {
    // Check for existing token on app start
    const token = localStorage.getItem('access_token');
    if (token) {
      try {
        const decoded: any = jwtDecode(token);
        // Check if token is expired
        if (decoded.exp * 1000 > Date.now()) {
          // Fetch user profile instead of decoding from token
          authService.getProfile()
            .then(response => {
              if (response.data?.user) {
                setUser(response.data.user);
              } else {
                localStorage.removeItem('access_token');
              }
            })
            .catch(error => {
              localStorage.removeItem('access_token');
            })
            .finally(() => {
              setLoading(false);
            });
        } else {
          localStorage.removeItem('access_token');
          setLoading(false);
        }
      } catch (error) {
        localStorage.removeItem('access_token');
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await authService.login(email, password);
      if (response.data) {
        const { access_token, user: userData } = response.data;

        // Store token
        localStorage.setItem('access_token', access_token);

        // Set user state
        setUser(userData);

        setSessionLoading(true);
        // Simulate a network request to prepare the session
        setTimeout(() => {
          setSessionLoading(false);
        }, 2000);
        return { success: true };
      }
      return { success: false, error: response.error || 'Login failed' };
    } catch (error: any) {
      console.error('Login failed:', error);

      // Handle rate limiting
      if (error.rateLimited) {
        return { success: false, error: error.userMessage || 'Too many login attempts. Please wait.' };
      }

      return { success: false, error: error.response?.data?.error || 'Login failed' };
    }
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    setUser(null);
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    login,
    logout,
    loading,
    sessionLoading,
    setSessionLoading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};